import React, { useState } from "react";
import ModalWrapper from "../modals/ModalWrapper";
import { Button } from "../buttons";

/**
 * UserMultiSelect - Generic component for selecting multiple users
 * 
 * Displays a box with selected users as pills. Clicking opens a modal
 * with checkboxes to select/deselect users.
 * 
 * @param {Object} props
 * @param {Array} props.users - Array of all available users (from context)
 * @param {Array} props.selectedUserIds - Array of currently selected user IDs
 * @param {Function} props.onChange - Callback when selection changes, receives array of user IDs
 * @param {string} [props.title="Watchers"] - Title displayed in the box
 * @param {string} [props.subtitle="Click to set"] - Subtitle displayed in the box
 * @param {string} [props.helpText] - Optional help text displayed below the box
 * @param {boolean} [props.disabled=false] - Whether the component is disabled
 */
const UserMultiSelect = ({
  users = [],
  selectedUserIds = [],
  onChange,
  title = "Watchers",
  subtitle = "(Click to set)",
  helpText,
  disabled = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState(new Set(selectedUserIds));

  // Initialize temp selection when modal opens
  const handleOpenModal = () => {
    if (disabled) return;
    setTempSelection(new Set(selectedUserIds));
    setIsModalOpen(true);
  };

  // Handle checkbox toggle in modal
  const handleToggleUser = (userId) => {
    const newSelection = new Set(tempSelection);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setTempSelection(newSelection);
  };

  // Save selection and close modal
  const handleSave = () => {
    onChange(Array.from(tempSelection));
    setIsModalOpen(false);
  };

  // Cancel and revert to original selection
  const handleCancel = () => {
    setTempSelection(new Set(selectedUserIds));
    setIsModalOpen(false);
  };

  // Get selected users for display
  const selectedUsers = users.filter((user) =>
    selectedUserIds.includes(user.id)
  );

  return (
    <>
      <div className="border-b pb-4">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-700">{title}</h2>
            <i className="text-xs text-gray-500">{subtitle}</i>
          </div>
        </div>

        {/* Selected users pills */}
        <div
          onClick={handleOpenModal}
          className={`min-h-42px] p-3 border border-gray-300 rounded-md ${disabled
            ? "bg-gray-50 cursor-not-allowed"
            : "bg-white cursor-pointer hover:border-blue-500 hover:bg-blue-50"
            } transition-colors`}
        >
          {selectedUsers.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No users selected</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map((user) => (
                <span
                  key={user.id}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                >
                  {user.first} {user.last}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Help text */}
        {helpText && (
          <p className="text-xs text-gray-500 italic mt-2">{helpText}</p>
        )}
      </div>

      {/* Selection Modal */}
      <ModalWrapper isOpen={isModalOpen} onClose={handleCancel}>
        <div className="p-4">
          <h2 className="text-xl font-bold text-gray-800 mb-4">{title}</h2>

          <div className="max-h-[400px] overflow-y-auto mb-4">
            {users.length === 0 ? (
              <p className="text-gray-500">No users available</p>
            ) : (
              <div className="space-y-0">
                {users.map((user) => {
                  const isSelected = tempSelection.has(user.id);
                  return (
                    <label
                      key={user.id}
                      className="flex items-center p-1 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleUser(user.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-1 text-gray-700">
                        {user.first} {user.last}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="custom"
              text="Cancel"
              onClick={handleCancel}
              className="bg-gray-300 px-2 hover:bg-gray-400"
            />
            <Button
              variant="primary"
              text="Confirm"
              onClick={handleSave}
            />
          </div>
        </div>
      </ModalWrapper>
    </>
  );
};

export default UserMultiSelect;
